/**
 * What a room's ⋯ offers, on its card and on its page: edit the page, change the time of a live, walk it through,
 * preview, copy the public link, invite people, unpublish and delete. Delete asks first; the computer's unpublish does not,
 * because everyone already registered keeps their page, replay and follow-ups (Season's sheet says so before it goes). A draft offers Delete alone: the rest is on
 * its board.
 */
import { useState } from "react";
import { useNavigate } from "react-router";
import type { WebinarSummary } from "../../../shared/types";
import { boardPath } from "../../board/paths";
import { InviteSheet, invitable, inviteTargetOf } from "../../components/InviteSheet";
import { Button } from "../../components/ui/Button";
import { Menu, type MenuItem } from "../../components/ui/Menu";
import { Sheet } from "../../components/ui/Sheet";
import { useToast } from "../../components/ui/Toast";
import { ApiError, api } from "../../lib/api";
import { type LiveLike, tabPath } from "./roomWords";

export type RoomForActions = Pick<WebinarSummary, "id" | "title" | "status" | "board" | "quick_live" | "register_url"> & {
  live?: LiveLike | undefined;
};

/** A live whose one session can still move: scheduled, not a series, not a quick live. */
export function movableLive(w: Pick<RoomForActions, "live" | "quick_live">) {
  return w.live?.state === "scheduled" && !w.live.series && !w.quick_live ? w.live : null;
}

/** What the room page's state says when it is opened to change a live's time. */
export const CHANGE_TIME_STATE = { changeTime: true } as const;

/** Copy a room's public link. A button that says Copy copies, on a laptop and on a phone alike. */
export async function copyRoomLink(url: string, toast: (m: string) => void): Promise<void> {
  try {
    await navigator.clipboard.writeText(url);
    toast("Link copied");
  } catch {
    toast("We couldn't copy the link");
  }
}

/** Delete and unpublish, with the toasts and the busy flag the computer's menu and Season's sheet share. */
export function useRoomActions(
  w: Pick<RoomForActions, "id" | "status">,
  { onChanged, onDeleted, onDone }: { onChanged: () => void; onDeleted?: (() => void) | undefined; onDone?: (() => void) | undefined },
) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const draft = w.status === "draft";

  const remove = () => {
    if (busy) return;
    setBusy(true);
    api.webinars
      .remove(w.id)
      .then(() => {
        onDone?.();
        toast(draft ? "Draft deleted" : "Room deleted");
        onDeleted?.();
        onChanged();
      })
      .catch((err: unknown) => toast(err instanceof ApiError ? err.message : "We couldn't delete that room"))
      .finally(() => setBusy(false));
  };

  const unpublish = () => {
    if (busy) return;
    setBusy(true);
    api.board
      .unpublish(w.id)
      .then(() => {
        onDone?.();
        toast("Unpublished. Everyone registered keeps their page.");
        onChanged();
      })
      .catch((err: unknown) => toast(err instanceof ApiError ? err.message : "We couldn't unpublish that page"))
      .finally(() => setBusy(false));
  };

  return { busy, remove, unpublish };
}

/** What delete says before it goes ahead: the sheet's title and its one line. */
export function deleteWords(draft: boolean): { title: string; text: string } {
  return draft
    ? { title: "Delete this draft?", text: "Its video, page and transcript go with it." }
    : { title: "Delete this room?", text: "Everyone who registered goes with it: their pages and links, every conversation, the recording. No undo." };
}

/**
 * The items a room's menu offers, as the computer's popover and Season's sheet both draw them. `actions` is what
 * the page does for each; `editLabel` is what the page-tab item is called where "Edit the page" is not offered.
 */
export function roomMenuItems(
  w: RoomForActions,
  actions: {
    toTab: (tab: "page" | "sessions") => void;
    changeTime?: (() => void) | undefined;
    walk: () => void;
    invite: () => void;
    unpublish: () => void;
    askDelete: () => void;
    copy: () => void;
    busy: boolean;
    editLabel?: string;
    studio?: (() => void) | undefined;
  },
): Array<MenuItem | "sep"> {
  const items: Array<MenuItem | "sep"> = [];
  const published = w.status === "published";
  const series = !!w.live?.series && !w.live.series.ended;
  if (w.status !== "draft") {
    if (!w.quick_live) items.push({ label: actions.editLabel ?? "Edit the page", icon: "pencil", onSelect: () => actions.toTab("page") });
    if (series) items.push({ label: "Change the time", icon: "cal", onSelect: () => actions.toTab("sessions") });
    else if (movableLive(w) && actions.changeTime) items.push({ label: "Change the time", icon: "cal", onSelect: actions.changeTime });
    if (w.board) items.push({ label: "Walk it through", icon: "list", onSelect: actions.walk });
    if (actions.studio) items.push({ label: "Open the studio", icon: "live", onSelect: actions.studio });
    if (published) {
      items.push({ label: "Preview", icon: "eye", onSelect: () => window.open(w.register_url, "_blank", "noopener") });
      items.push({ label: "Copy public link", icon: "link", onSelect: actions.copy });
    }
    if (invitable(w) && !w.quick_live) items.push({ label: "Invite people", icon: "userplus", onSelect: actions.invite });
    items.push("sep");
    if (published) items.push({ label: "Unpublish", icon: "pause", onSelect: actions.unpublish, disabled: actions.busy });
  }
  items.push({ label: "Delete", icon: "trash", danger: true, onSelect: actions.askDelete });
  return items;
}

/** The ⋯ button with its menu, its confirm and the invite sheet. */
export function RoomMenu({
  room: w,
  onChanged,
  onDeleted,
  onChangeTime,
  onTab,
  bordered = false,
}: {
  room: RoomForActions;
  onChanged: () => void;
  /** Where to go once it is gone; the list stays put. */
  onDeleted?: () => void;
  /** On the room's own page, the time is changed right there. */
  onChangeTime?: () => void;
  /** On the room's own page, a tab is a tab away. */
  onTab?: (tab: "page" | "sessions") => void;
  bordered?: boolean;
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const [confirming, setConfirming] = useState(false);
  const [inviting, setInviting] = useState(false);
  const draft = w.status === "draft";
  const { busy, remove, unpublish } = useRoomActions(w, { onChanged, onDeleted, onDone: () => setConfirming(false) });
  const words = deleteWords(draft);

  const items = roomMenuItems(w, {
    toTab: (tab) => (onTab ? onTab(tab) : navigate(tabPath(w.id, tab))),
    changeTime: () => (onChangeTime ? onChangeTime() : navigate(`/rooms/${w.id}`, { state: CHANGE_TIME_STATE })),
    walk: () => navigate(boardPath(w.id)),
    invite: () => setInviting(true),
    unpublish,
    askDelete: () => setConfirming(true),
    copy: () => void copyRoomLink(w.register_url, toast),
    busy,
  });

  return (
    // Why the wrapper stops clicks: the card around it opens the room, and the menu and sheets are portalled
    // children whose clicks still bubble through it in React.
    <span className="room-menu" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
      <Menu items={items} label={`More for ${w.title}`} buttonClassName={bordered ? "ibtn bd" : "ibtn"} />
      <Sheet
        open={confirming}
        onClose={() => setConfirming(false)}
        title={words.title}
        sub={w.title}
        footer={
          <>
            <Button onClick={() => setConfirming(false)}>Keep it</Button>
            <Button variant="p" className="dangerb" onClick={remove} disabled={busy}>
              {busy ? "Deleting…" : "Delete"}
            </Button>
          </>
        }
      >
        <p className="ink2">{words.text}</p>
      </Sheet>
      {inviting ? (
        <InviteSheet
          webinar={inviteTargetOf(w)}
          onClose={() => setInviting(false)}
          onSent={(send) => toast(`Sending ${send.total.toLocaleString("en-US")} ${send.total === 1 ? "invite" : "invites"}`)}
          onToast={toast}
        />
      ) : null}
    </span>
  );
}
